import React, { useState, useEffect } from 'react';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import './SummaryCards.css';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';

const SummaryCards = () => {
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [currentMonthRevenue, setCurrentMonthRevenue] = useState(0);
  const [previousMonthlyRevenue, setPreviousMonthlyRevenue] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [subscribers, setSubscribers] = useState({
    user: 0,
    premium: 0,
    totalOrders: 0,
    currentMonthOrders: 0,
    previousMonthOrders: 0
  });

  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [loadingSales, setLoadingSales] = useState(true);
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [leadAdminStats, setLeadAdminStats] = useState({
    totalCreditsGranted: 0,
    totalCreditsUsed: 0,
    totalLeadsUnlocked: 0,
  });
  const [loadingLeadAdmin, setLoadingLeadAdmin] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token =
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('authToken');

    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const fetchTotalProducts = async () => {
      try {
        setLoadingProducts(true);
        const res = await fetch('/api/v1/get-total-products', { method: 'GET', headers, credentials: 'include' });
        if (!res.ok) throw new Error(`Products API error: ${res.status}`);
        const data = await res.json();
        setTotalProducts(data.total || data.count || data.totalProducts || 0);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoadingProducts(false);
      }
    };

    const fetchRevenueData = async () => {
      try {
        setLoadingRevenue(true);
        const res = await fetch('/api/v1/total-revenue', { method: 'GET', headers, credentials: 'include' });
        if (!res.ok) throw new Error(`Revenue API error: ${res.status}`);
        const data = await res.json();
        setTotalRevenue(data.totalRevenue || 0);
        setCurrentMonthRevenue(data.currentMonthRevenue || 0);
        setPreviousMonthlyRevenue(data.previousMonthRevenue || 0);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoadingRevenue(false);
      }
    };

    const fetchSalesCount = async () => {
      try {
        setLoadingSales(true);
        const res = await fetch('/api/v1/get-subscribe-user-count', { method: 'GET', headers, credentials: 'include' });
        if (!res.ok) throw new Error(`Subscribers API error: ${res.status}`);
        const data = await res.json();

        let userCount = 0;
        let premiumCount = 0;
        if (Array.isArray(data.roleCounts)) {
          data.roleCounts.forEach((item) => {
            const role = item.role?.toLowerCase();
            const count = parseInt(item.count, 10) || 0;
            if (role === 'user') userCount = count;
            if (role === 'premium') premiumCount = count;
          });
        }

        setSubscribers((prev) => ({ ...prev, user: userCount, premium: premiumCount }));
        setSalesCount(userCount + premiumCount);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoadingSales(false);
      }
    };

    const fetchSubscribers = async () => {
      try {
        setLoadingSubs(true);
        const res = await fetch('/api/v1/get-total-orders', { method: 'GET', headers, credentials: 'include' });
        if (!res.ok) throw new Error(`Orders API error: ${res.status}`);
        const data = await res.json();
        setSubscribers((prev) => ({
          ...prev,
          totalOrders: data.totalOrders || 0,
          currentMonthOrders: data.currentMonthOrders || 0,
          previousMonthOrders: data.previousMonthOrders || 0
        }));
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoadingSubs(false);
      }
    };

    const fetchLeadAdminStats = async () => {
      try {
        setLoadingLeadAdmin(true);
        const res = await fetch('/api/v1/lead-credits/admin/analytics', { method: 'GET', headers, credentials: 'include' });
        if (!res.ok) throw new Error(`Lead Admin API error: ${res.status}`);
        const data = await res.json();
        if (data.success) {
          setLeadAdminStats(data.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingLeadAdmin(false);
      }
    };

    fetchTotalProducts();
    fetchRevenueData();
    fetchSalesCount();
    fetchSubscribers();
    fetchLeadAdminStats();
  }, []);

  const revenueChange =
    previousMonthlyRevenue > 0
      ? (((currentMonthRevenue - previousMonthlyRevenue) / previousMonthlyRevenue) * 100).toFixed(2)
      : null;

  const premiumVsUserPercentage =
    subscribers.user > 0
      ? (((subscribers.premium - subscribers.user) / subscribers.user) * 100).toFixed(1)
      : subscribers.premium > 0
        ? 100
        : 0;

  const subsChange =
    subscribers.previousMonthOrders > 0
      ? (((subscribers.currentMonthOrders - subscribers.previousMonthOrders) / subscribers.previousMonthOrders) * 100).toFixed(2)
      : null;

  const cardData = [
    {
      title: 'Total Products',
      value: loadingProducts ? 'Loading...' : totalProducts,
      subtitle: '',
      iconClass: 'icon-primary',
      iconText: <ProductionQuantityLimitsIcon style={{ color: '#007bff' }} />, // Blue
    },
    {
      title: 'Total Subs',
      value: loadingSales ? 'Loading...' : salesCount,
      subtitle: 'User + Premium',
      iconClass: 'icon-success',
      iconText: <SubtitlesIcon style={{ color: '#28a745' }} />, // Green
      trend: {
        text: `${premiumVsUserPercentage > 0 ? '+' : ''}${premiumVsUserPercentage}%`,
        isPositive: premiumVsUserPercentage >= 0,
        tooltip: `Premium: ${subscribers.premium}, User: ${subscribers.user}`
      },
    },
    {
      title: 'Sales Count',
      value: loadingSubs ? 'Loading...' : subscribers.totalOrders,
      subtitle: 'Total Orders',
      iconClass: 'icon-secondary',
      iconText: <PointOfSaleIcon style={{ color: '#6c757d' }} />, // Grey
      trend: subsChange !== null
        ? {
          text: `${subsChange > 0 ? '+' : ''}${subsChange}%`,
          isPositive: subsChange >= 0,
          tooltip: `CurrentMon: ${subscribers.currentMonthOrders}, PreviousMon: ${subscribers.previousMonthOrders}`
        }
        : null,
    },
    {
      title: 'Total Revenue',
      value: loadingRevenue ? 'Loading...' : `$${totalRevenue.toLocaleString()}`,
      subtitle: 'All-time total',
      iconClass: 'icon-warning',
      iconText: <AssuredWorkloadIcon style={{ color: '#ffc107' }} />, // Yellow/Orange
      trend: revenueChange !== null
        ? {
          text: `${revenueChange > 0 ? '+' : ''}${revenueChange}%`,
          isPositive: revenueChange > 0,
          tooltip: `Current: $${currentMonthRevenue.toLocaleString()}, Previous: $${previousMonthlyRevenue.toLocaleString()}`
        }
        : null,
    },
    {
      title: 'Credits Used',
      value: loadingLeadAdmin ? 'Loading...' : leadAdminStats.totalCreditsUsed,
      subtitle: 'Global System',
      iconClass: 'icon-primary',
      iconText: <PointOfSaleIcon style={{ color: '#007bff' }} />,
    },
    {
      title: 'Leads Unlocked',
      value: loadingLeadAdmin ? 'Loading...' : leadAdminStats.totalLeadsUnlocked,
      subtitle: 'Global System',
      iconClass: 'icon-success',
      iconText: <SubtitlesIcon style={{ color: '#28a745' }} />,
    },
  ];

  return (
    <div className="summary-cards-container">
      {cardData.map(({ title, value, subtitle, iconClass, iconText, trend }, index) => (
        <div key={index} className="summary-card">
          <div className={`summary-card-icon ${iconClass}`}>{iconText}</div>
          <div className="summary-card-content">
            <p className="summary-card-title">{title}</p>
            <h5 className="summary-card-value">{value}</h5>
            {subtitle && <p className="summary-card-subtitle">{subtitle}</p>}
          </div>
          {trend && (
            <div className="summary-card-trend-wrapper">
              <p
                className="summary-card-trend"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  color: trend.isPositive ? '#22c55e' : '#ef4444',
                }}
              >
                {trend.isPositive ? (
                  <TrendingUpIcon fontSize="small" style={{ color: '#22c55e' }} />
                ) : (
                  <TrendingDownIcon fontSize="small" style={{ color: '#ef4444' }} />
                )}
                {trend.text}
              </p>
              {trend.tooltip && (
                <span className="custom-tooltip">{trend.tooltip}</span>
              )}
            </div>
          )}
        </div>
      ))}
      {error && <p className="summary-error">Error: {error}</p>}
    </div>
  );
};

export default SummaryCards;